import {
  configMgmtControllerListRequiredStudyTypes,
  configMgmtControllerListTahseelStudyRequestTypes,
  studyRequestsControllerMockTahseelRequest,
} from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Shuffle } from 'lucide-react';
import { useState } from 'react';
import { FormField } from '../../components/forms/form-field';
import { AppModal } from '../../components/ui/app-modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface CodeOption {
  id: string;
  code: string;
  name: string;
}

const RANDOM_COMPANY_NAMES = [
  'Al Noor Trading LLC',
  'Sharjah Coastal Works',
  'Blue Dune Facilities',
  'Corniche Industrial Group',
  'Falcon Ridge Enterprises',
];

function randomOf<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

export function MockTahseelRequestModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [studyTypeCode, setStudyTypeCode] = useState<string | undefined>(undefined);
  const [requiredStudyCode, setRequiredStudyCode] = useState<string | undefined>(undefined);
  const [serviceAmount, setServiceAmount] = useState('');

  const { data: tahseelStudyRequestTypes = [] } = useQuery<CodeOption[]>({
    queryKey: ['globalTahseelStudyRequestTypes'],
    queryFn: () =>
      configMgmtControllerListTahseelStudyRequestTypes() as unknown as Promise<CodeOption[]>,
    enabled: open,
  });

  const { data: requiredStudyTypes = [] } = useQuery<CodeOption[]>({
    queryKey: ['globalRequiredStudyTypes'],
    queryFn: () => configMgmtControllerListRequiredStudyTypes() as unknown as Promise<CodeOption[]>,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      studyRequestsControllerMockTahseelRequest({
        requestBody: {
          companyName: companyName || undefined,
          email: email || undefined,
          studyTypeCode,
          requiredStudyCode,
          serviceAmount: serviceAmount ? Number(serviceAmount) : undefined,
        },
      }) as unknown as Promise<{ studyRequestId: string }>,
    onSuccess: (result) => {
      onClose();
      navigate({
        to: '/study-requests/$requestId',
        params: { requestId: result.studyRequestId },
      });
    },
  });

  function fillRandom() {
    const stamp = Date.now();
    const name = randomOf(RANDOM_COMPANY_NAMES);
    setCompanyName(`${name} ${stamp}`);
    setEmail(`mock.${stamp}@example.com`);
    setServiceAmount(String(500 + Math.floor(Math.random() * 4500)));
  }

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Mock Tahseel Request"
      description="Dev/QA only — simulates a Tahseel inbound request without waiting on the external system."
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={fillRandom}>
            <Shuffle size={13} />
            Generate Random Data
          </Button>
        </div>

        <FormField label="Company Name" hint="Optional — a name is generated if left blank">
          <Input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="e.g. Al Noor Trading LLC"
          />
        </FormField>

        <FormField label="Email" hint="Optional — an email is generated if left blank">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. contact@company.com"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Study Type">
            <Select value={studyTypeCode} onValueChange={setStudyTypeCode}>
              <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                {tahseelStudyRequestTypes.map((st) => (
                  <SelectItem key={st.id} value={st.code}>
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Required Study">
            <Select value={requiredStudyCode} onValueChange={setRequiredStudyCode}>
              <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                {requiredStudyTypes.map((rst) => (
                  <SelectItem key={rst.id} value={rst.code}>
                    {rst.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <FormField label="Service Amount (AED)" hint="Optional — defaults to 1000">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={serviceAmount}
            onChange={(e) => setServiceAmount(e.target.value)}
            placeholder="1000"
          />
        </FormField>

        {mutation.isError && (
          <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
            Failed to create mock request.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create Mock Request'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}
